import { ProjectRunAnalysisQuestion } from '../../Types/dealGPT';
import { AnalysisQuestionStatus } from '../../Types/enums';

export const getLatestUniqueQuestions = (
  questions: ProjectRunAnalysisQuestion[]
): ProjectRunAnalysisQuestion[] => {
  const latestQuestionsMap: Map<string, ProjectRunAnalysisQuestion> = new Map();
  questions.forEach((question) => {
    const key = `${question.sequence}-${question.displayOrder}`;
    const existingQuestion = latestQuestionsMap.get(key);

    if (
      !existingQuestion ||
      (question.createdAt &&
        existingQuestion &&
        existingQuestion.createdAt &&
        new Date(question.createdAt) > new Date(existingQuestion.createdAt))
    ) {
      latestQuestionsMap.set(key, question);
    }
  });

  return Array.from(latestQuestionsMap.values());
};

export const sortQuestions = (
  questions: ProjectRunAnalysisQuestion[]
): ProjectRunAnalysisQuestion[] => {
  return questions.sort((a, b) => {
    // First compare by sequence
    if (a.sequence !== b.sequence) {
      return a.sequence - b.sequence;
    }
    // Project level questions should always come first
    if (a.scope === 'PROJECT' && b.scope !== 'PROJECT') {
      return -1;
    } else if (a.scope !== 'PROJECT' && b.scope === 'PROJECT') {
      return 1;
    }
    // If sequence is the same, compare by displayOrder (handling undefined cases)
    if (a.displayOrder && b.displayOrder) {
      return a.displayOrder - b.displayOrder;
    }
    // If one of the displayOrders is undefined, place the defined one first
    if (!a.displayOrder) {
      return 1;
    }
    if (!b.displayOrder) {
      return -1;
    }
    // If both are undefined, they are considered equal
    return 0;
  });
};

export const KPIStatusColors: Record<AnalysisQuestionStatus, string | undefined> = {
  [AnalysisQuestionStatus.NEEDS_CONFIRMATION]: '#FFC700',
  [AnalysisQuestionStatus.NOT_FOUND]: '#f52c00',
  [AnalysisQuestionStatus.CONFIRMED]: '#0047FF',
  [AnalysisQuestionStatus.ANSWERED]: undefined,
  [AnalysisQuestionStatus.ERROR]: undefined,
  [AnalysisQuestionStatus.PENDING]: undefined,
  [AnalysisQuestionStatus.INITIAL]: undefined,
  [AnalysisQuestionStatus.INPUT_PENDING]: undefined,
};

export const KPIStatusExplanations: Record<AnalysisQuestionStatus, string | undefined> = {
  [AnalysisQuestionStatus.NEEDS_CONFIRMATION]: 'Answer Found in Content, Must Validate and Confirm',
  [AnalysisQuestionStatus.NOT_FOUND]: 'Missing. No Answer Found',
  [AnalysisQuestionStatus.CONFIRMED]: 'Confirmed Answer',
  [AnalysisQuestionStatus.ANSWERED]: undefined,
  [AnalysisQuestionStatus.ERROR]: undefined,
  [AnalysisQuestionStatus.PENDING]: undefined,
  [AnalysisQuestionStatus.INITIAL]: undefined,
  [AnalysisQuestionStatus.INPUT_PENDING]: undefined,
};

const appendEnvironment = (title: string) => {
  let newTitle = title;
  if (import.meta.env.VITE_ENVIRONMENT === 'demo') {
    newTitle = `${title} - demo`;
  } else if (import.meta.env.VITE_ENVIRONMENT === 'staging') {
    newTitle = `${title} - staging`;
  } else if (import.meta.env.VITE_ENVIRONMENT === 'dev') {
    newTitle = `${title} - dev`;
  } else if (import.meta.env.VITE_ENVIRONMENT === 'local') {
    newTitle = `${title} - local`;
  }
  return newTitle;
};

export interface setPageTitleProps {
  projectId?: string;
  projectName?: string;
}

export function setPageTitle(props?: setPageTitleProps) {
  const titleElement = document.getElementById('title');
  const iconElement = document.getElementById('icon') as HTMLLinkElement;
  if (titleElement) {
    if (
      import.meta.env.VITE_EXPERTS_REDIRECT_ORIGIN_URI &&
      window.location.origin === import.meta.env.VITE_EXPERTS_REDIRECT_ORIGIN_URI
    ) {
      titleElement.innerText = 'Experts';
    } else {
      if (window.location.pathname.includes('intellio-advantage')) {
        let title = 'Intellio Advantage';
        if (props?.projectId && props?.projectName) {
          title += ` - ${props.projectName}`;
        }
        titleElement.innerText = appendEnvironment(title);
      } else {
        titleElement.innerText = appendEnvironment('Nigel');
        if (
          import.meta.env.VITE_ENVIRONMENT !== 'prod' &&
          import.meta.env.VITE_ENVIRONMENT !== 'demo'
        ) {
          iconElement.href = '/favicondev.png';
        }
      }
    }
  }
}

export const getBooleanFromLocalStorage = (key: string, defaultValue = false) => {
  const item = localStorage.getItem(key);
  if (item === null) {
    return defaultValue;
  }
  return item === 'true';
};
